import React from "react";
import { Badge } from "@amzn/awsui-components-react";
import { SideNavigationProps } from "@amzn/awsui-components-react/polaris/side-navigation";
import { useAppSelector } from "app/store";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Team } from "models/schemas";
import { useHeaderTeamNameSelector } from "pages/team_settings/hooks";

type NavItem = {
  type: "link" | "divider";
  text?: string;
  href?: string;
  feature?: string;
  visibleToMember?: boolean;
  visibleToGuest?: boolean;
};

/**
 * Hook for handling side navigation events
 */
export const useOnFollowHandler = () => {
  const navigate = useNavigate();

  return (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
    if (!event.detail.external) {
      event.preventDefault();
      navigate(event.detail.href, { replace: true });
    }
  };
};

export const useOrderedNavItems = () => {
  const { t } = useTranslation();
  const teamName = useHeaderTeamNameSelector();
  const allTeams = useAppSelector((state) => state.teamSettings?.allTeams);

  const isLoading = !allTeams || !teamName;

  const selectedTeamFeatures = new Set<string>(
    (allTeams && "teams" in allTeams ? (allTeams as any).teams : []).find(
      (team: Team) => team?.teamId == teamName
    )?.enabledFeatures || []
  );

  const orderedNavItems: NavItem[] = [
    {
      type: "link",
      text: t("pages.audits.title"),
      href: "/audits/",
      feature: "AUDIT",
      visibleToGuest: true,
    },
    {
      type: "link",
      text: "Notices",
      href: "/notices/",
      feature: "NOTICE",
      visibleToGuest: true,
    },
    {
      type: "link",
      text: "Litigations",
      href: "/litigations/",
      feature: "LITIGATION",
      visibleToGuest: true,
    },
    { type: "divider", visibleToMember: true, visibleToGuest: true },
    {
      type: "link",
      text: t("pages.tasks.title"),
      href: "/tasks",
      visibleToMember: true,
      visibleToGuest: true,
    },
    {
      type: "link",
      text: t("pages.reviews.title"),
      href: "/reviews",
      visibleToMember: true,
      visibleToGuest: true,
    },
    {
      type: "link",
      text: t("pages.drafts.title"),
      href: "/drafts",
      feature: "FILE_SCAN",
    },
    {
      type: "link",
      text: t("pages.data.title"),
      href: "/data/",
      visibleToMember: true,
    },
    {
      type: "link",
      text: t("pages.events.title"),
      href: "/events/",
      feature: "EVENT",
      visibleToGuest: false,
    },
    { type: "divider", visibleToMember: true },
    {
      type: "link",
      text: "Dashboard",
      href: "/dashboard/",
      feature: "DASHBOARD",
    },
    {
      type: "link",
      text: t("pages.gaia_ai.title"),
      href: "/gaia-ai/",
      feature: "GAIA_AI_KB",
    },
    {
      type: "link",
      text: t("pages.gaia_ai.chatbot.title"),
      href: "/gaia-ai/chatbot",
      feature: "CHATBOT",
    },
    {
      type: "link",
      text: t("pages.team_files.title"),
      href: "/team-files",
      visibleToMember: true,
    },
    {
      type: "link",
      text: t("pages.team_settings.title"),
      href: "/team",
      visibleToMember: true,
    },
  ];

  const navigationItems = orderedNavItems
    .filter(
      (item: NavItem) =>
        (teamName !== "guest" && item.visibleToMember) ||
        (teamName === "guest" && item.visibleToGuest) ||
        (item.feature && selectedTeamFeatures.has(item.feature))
    )
    .map((item): SideNavigationProps.Item => {
      if (item.type === "divider") {
        return { type: "divider" };
      }
      const text =
        item.feature === "CHATBOT"
          ? React.createElement(
              React.Fragment,
              null,
              item.text,
              " ",
              React.createElement(Badge, null, "beta")
            )
          : item.text!;
      return {
        type: "link",
        text: text as any,
        href: item.href!,
      };
    });

  return { items: navigationItems, isLoading };
};

export const useNavConfig = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { items, isLoading } = useOrderedNavItems();

  return {
    header: {
      href: "",
      text: t("left_nav.title"),
    } as SideNavigationProps.Header,
    items: isLoading ? [] : items,
    activeHref: location.pathname,
  };
};
